import { Image as ImageIcon, LayoutGrid, Music, Video } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { AttachmentKind } from "~/entities/chat";
import { cn } from "~/shared/lib/utils";
import { Tabs, TabsList, TabsTrigger } from "~/shared/ui/tabs";

import type { PickerTabValue } from "./pickerDialogModel";

interface PickerTabsRowProps {
    tab: PickerTabValue;
    onTabChange: (tab: PickerTabValue) => void;
    acceptedKinds: AttachmentKind[];
    i18nPrefix: string;
}

// Underline-стиль табов (как у заказчика на /assets): плоский ряд с
// иконкой+текстом, активный — violet-цвет и `border-b-2`, без фоновой
// подложки. Переопределяем shadcn-классы через className на
// TabsList/TabsTrigger.
export function PickerTabsRow({ tab, onTabChange, acceptedKinds, i18nPrefix }: PickerTabsRowProps) {
    const { t } = useTranslation("chat");
    return (
        <Tabs value={tab} onValueChange={(v) => onTabChange(v as PickerTabValue)}>
            <TabsList className="h-auto w-full justify-start gap-1 rounded-none border-b border-white/10 bg-transparent p-0">
                <PickerTab value="all" icon={LayoutGrid} label={t(`${i18nPrefix}.tabAll`)} />
                {acceptedKinds.includes("image") && (
                    <PickerTab value="image" icon={ImageIcon} label={t(`${i18nPrefix}.tabImages`)} />
                )}
                {acceptedKinds.includes("video") && (
                    <PickerTab value="video" icon={Video} label={t(`${i18nPrefix}.tabVideos`)} />
                )}
                {acceptedKinds.includes("audio") && (
                    <PickerTab value="audio" icon={Music} label={t(`${i18nPrefix}.tabAudio`)} />
                )}
            </TabsList>
        </Tabs>
    );
}

interface PickerTabProps {
    value: PickerTabValue;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
}

// Underline-таб: иконка + текст, активный violet с border-bottom.
// shadcn TabsTrigger по дефолту даёт «pill»-стиль (фоновая подложка
// активного), мы переопределяем под design-language заказчика
// (см. mockups assets-page tabs: иконка слева, текст справа, тонкое
// подчёркивание для active).
function PickerTab({ value, icon: Icon, label }: PickerTabProps) {
    return (
        <TabsTrigger
            value={value}
            className={cn(
                // Сброс shadcn-дефолтов (rounded pill, фоновая подложка, shadow).
                "rounded-none bg-transparent shadow-none",
                // Border: только bottom=2px, остальные стороны 0.
                "border-0 border-b-2 border-b-transparent",
                // Layout: иконка + текст одной линией.
                "flex h-9 items-center gap-1.5 px-3 py-2 text-sm font-medium",
                // Цвета inactive.
                "text-zinc-400 transition-colors hover:text-zinc-200",
                // Active: фиолетовое подчёркивание + фиолетовый текст.
                // `!` префикс на border-b-violet-400 / text-violet-300 нужен
                // чтобы перебить shadcn-дефолты `dark:data-[state=active]:border-input`
                // (всех сторон) и `dark:data-[state=active]:text-foreground`.
                // Без `!` побеждает shadcn по специфичности `dark:` варианта.
                "data-[state=active]:!border-b-violet-400 data-[state=active]:!bg-transparent data-[state=active]:!text-violet-300 data-[state=active]:!shadow-none",
                // Жёстко глушим focus-visible-индикацию: outline (от
                // глобального `.app-root :focus-visible`) и ring/shadow
                // (от shadcn-дефолтов TabsTrigger). `&` повышает specificity
                // до 0,2,1 — гарантированно перебивает оба источника без
                // зависимости от порядка CSS-файлов. Border здесь НЕ трогаем,
                // иначе active violet-underline тоже исчезнет.
                "[&:focus-visible]:!shadow-none [&:focus-visible]:!ring-0 [&:focus-visible]:!outline-none",
            )}
        >
            <Icon className="h-3.5 w-3.5" />
            {label}
        </TabsTrigger>
    );
}
